import { Announced, ISearchBoxStyles, SearchBox, Stack, format } from "@fluentui/react";
import * as React from "react";
import { IRcaTest } from "../models/IRcaTest";
import { RcaTestCard } from "./RcaTestCard";
import { useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks";
import { loadTests, searchTests } from "../features/testSelection/testSelectionSlice";

/**
 * Route parameters for the test list page
 */
interface IRouteParams {
  /**
   * The test category from the URL
   */
  category: string;
}

/**
 * State supplied when navigating to the test list page.
 */
interface IRouteState {
  /**
   * Whether focus should move to the selected category heading.
   */
  focusCategoryHeading?: boolean;
}

/**
 * Checks whether navigation requested focus for the category heading.
 *
 * @param state - The route state to inspect.
 * @returns Whether category heading focus was requested.
 */
const shouldFocusCategoryHeading = (state: unknown): state is IRouteState =>
  typeof state === "object" &&
  state !== null &&
  "focusCategoryHeading" in state &&
  (state as Record<string, unknown>).focusCategoryHeading === true;

/**
 * The Test List page for test selection
 *
 * @param props - The component props
 * @returns - the component
 */
export const RcaTestListPage: React.FC<RouteComponentProps<IRouteParams>> = (props) => {
  const dispatch = useAppDispatch();
  const language = useAppSelector((state) => state.globalConfig.language);
  const searchQuery = useAppSelector((state) => state.testSelection.availableTestsSearchQuery);
  const availableTests = useAppSelector((state) => state.testSelection.availableTestsFiltered);
  const testCategories = useAppSelector((state) => state.testSelection.testCategories);
  const selectedTestCategory = props.match.params.category;
  const selectedTestCategoryName = testCategories.find((category) => category.id === selectedTestCategory)?.name;
  const categoryHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const focusedLocationKeyRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (selectedTestCategory) {
      void dispatch(loadTests({ language, category: selectedTestCategory }));
    }
  }, [dispatch, language, selectedTestCategory]);

  useEffect(() => {
    const locationKey = props.location.key ?? props.location.pathname;

    if (!shouldFocusCategoryHeading(props.location.state) || !selectedTestCategoryName || focusedLocationKeyRef.current === locationKey) {
      return;
    }

    focusedLocationKeyRef.current = locationKey;
    categoryHeadingRef.current?.focus();
  }, [props.location.key, props.location.pathname, props.location.state, selectedTestCategoryName]);

  const searchBoxStyles: Partial<ISearchBoxStyles> = {
    root: {
      marginTop: 15,
      marginLeft: 15,
      marginRight: 15,
      width: 300,
    },
  };

  return (
    <Stack verticalFill disableShrink styles={{ root: { height: "auto" } }}>
      {selectedTestCategoryName && (
        <h2 ref={categoryHeadingRef} tabIndex={-1} style={{ margin: "15px 15px 0" }}>
          {selectedTestCategoryName}
        </h2>
      )}
      <SearchBox
        styles={searchBoxStyles}
        placeholder={ClientStrings.TestSearchBoxPlaceholder}
        ariaLabel={ClientStrings.TestSearchBoxPlaceholder}
        clearButtonProps={{
          ariaLabel: ClientStrings.TestSearchBoxClearButtonLabel,
          title: ClientStrings.TestSearchBoxClearButtonLabel,
        }}
        defaultValue={searchQuery}
        onSearch={(query): void => {
          dispatch(searchTests(typeof query === "string" ? query : ""));
        }}
        onClear={(): void => {
          dispatch(searchTests(""));
        }}
      />
      {searchQuery && <Announced message={format(ClientStrings.TestSearchAriaMessage, availableTests.length)} />}
      <Stack
        horizontal
        wrap
        tokens={{ childrenGap: 15, padding: 15 }}
        styles={{
          inner: {
            width: "100%",
          },
        }}
      >
        {availableTests.map((t) => (
          <RcaTestCard
            key={t.id}
            test={t}
            navigateToTestInput={(test: IRcaTest): void => {
              if (test.inputVersion === 2) {
                props.history.push(`/tests/${test.id}/input`);
              } else {
                window.location.assign(`/Default.aspx?testId=${test.id}`);
              }
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export default RcaTestListPage;
